import {dateTime, fullDate, shortDate, time} from '../../../src/utils/dates.ts';
import {div} from '../../../src/utils/html.ts';
import type {GroupByValue} from '../../../src/utils/sort.ts';
import type {ArticleCardLayoutRecord, CardLayout, ItemStateValue} from '../../../types/types.ts';
import {layoutIcon} from '../../components/icons/actions.ts';
import {playIcon} from '../../components/icons/reel-icons.ts';
import {exitWithTransition} from '../../lib/animations.ts';
import {createElement} from '../../lib/dom.ts';
import type {Store} from '../../lib/store.ts';
import {Article, type RelatedArticle} from '../../models/article.ts';
import type {ReaderEdition, ReaderItem} from '../../models/reader-edition.ts';
import {Reel} from '../../models/reel.ts';
import {getAvailableLayoutsForCard} from './lib/card-layouts.ts';
import {Cards} from './lib/cards.ts';
import {setUpNestedScrollFix} from './lib/scroll-fix.ts';

type onActivateCallback = (listing: ReaderListing, pageNumber?: number, skipScroll?: boolean) => void;
type onStateChangeCallback = (activeListing: ReaderListing, state: ItemStateValue, toggle?: boolean) => void;

export class ReaderListing {
	public readonly id: string;
	public item: ReaderItem;
	public el: HTMLElement;
	public currentViewEl: HTMLElement;
	public frontPageEl: HTMLElement;
	public tocEntryEl: HTMLElement;
	public cardsEl: HTMLDivElement;
	public layoutMenuEl: HTMLDivElement;
	public deepDiveEl: HTMLDivElement;
	public previousListing: ReaderListing | undefined;
	public nextListing: ReaderListing | undefined;
	public number: number;
	public isActive = false;
	public groupByValue?: GroupByValue;
	public loadArticlePromise?: Promise<ReaderItem | null>;
	public renderPromise?: Promise<true>;

	private readonly onActivate: onActivateCallback;
	private readonly onStateChange: onStateChangeCallback;
	private readonly store: Store;
	private cards: Cards;
	private mobileCardLayouts: ArticleCardLayoutRecord = {};
	private desktopCardLayouts: ArticleCardLayoutRecord = {};
	private readerEdition: ReaderEdition;
	private mobileMode: MediaQueryList;
	private cleanupScrollFix: (() => void) | null = null;

	constructor(
		item: ReaderItem,
		readerEdition: ReaderEdition,
		number: number,
		store: Store,
		currentViewEl: HTMLElement,
		onActivate: onActivateCallback,
		onStateChange: onStateChangeCallback,
		mobileMode: MediaQueryList
	) {
		this.item = item;
		this.readerEdition = readerEdition;
		this.number = number;
		this.id = item.id;
		this.el = document.createElement('div');
		this.cardsEl = document.createElement('div');
		this.tocEntryEl = document.createElement('div');
		this.frontPageEl = document.createElement('div');
		this.deepDiveEl = document.createElement('div');
		this.layoutMenuEl = document.createElement('div');
		this.layoutMenuEl.hidden = true;
		this.layoutMenuEl.classList.add('layout-menu');
		this.store = store;
		this.currentViewEl = currentViewEl;
		this.onActivate = onActivate;
		this.onStateChange = onStateChange;
		this.cards = new Cards(item, this.cardsEl, this.el, mobileMode, store);
		this.mobileMode = mobileMode;
		if (item.cardLayouts) {
			this.desktopCardLayouts = item.cardLayouts.desktop;
			this.mobileCardLayouts = item.cardLayouts.mobile;
		} else {
			const layoutsData = this.store.getArticleCardLayouts(this.item.id);
			this.desktopCardLayouts = layoutsData?.desktop || {};
			this.mobileCardLayouts = layoutsData?.mobile || {};
		}

		this.registerEventListeners();
	}

	get cardCount() {
		return this.cards.cardCount;
	}

	renderTocEntry() {
		this.tocEntryEl.classList.add('toc-entry');
		let storyIcon = '';
		if (this.item instanceof Reel) {
			this.tocEntryEl.classList.add('toc-entry--reel');
			storyIcon = playIcon;
		}
		this.tocEntryEl.innerHTML = `
		${assetHtml(this.item)}
		<div class="toc-entry__date">${shortDate(this.item.publishedAt, this.readerEdition.locale) || ''}</div>
		<div class="toc-entry__time">${(this.item.publishedAt && time(this.item.publishedAt, this.readerEdition.locale)) || ''}</div>
		<div class="toc-entry__story">${storyIcon}${this.item.story || ''}</div>
		<div class="toc-entry__title">${this.item.listTitle || this.item.pretitle || `No title: ${this.id}`}</div>
		<div class="toc-entry__length"><span class="seen"></span><span class="unseen"></span></div>
`;

		this.tocEntryEl.dataset.section = this.item.category || this.item.section || 'No section';
		this.applyArticleState();
		this.updateTocDots();

		function assetHtml(item: ReaderItem) {
			if (!(item instanceof Article)) {
				return '';
			}

			if (!item.asset?.thumbnailUrl) {
				return '';
			}

			if (item.size > 4000) {
				return `<div class="toc-entry__asset"><img src="${item.asset.thumbnailUrl}"/></div>`;
			}

			return '';
		}
	}

	renderFrontPageEntry(number: number) {
		this.frontPageEl.classList.add('teaser');
		const category = this.item.category || this.item.section || '';
		this.frontPageEl.dataset.category = category.toLocaleLowerCase();

		this.frontPageEl.innerHTML = `
<div class="teaser__asset">${this.item.asset?.pictureHtml || ''}</div>
<div class="teaser__story">${this.item.story}</div>
<div class="teaser__title">${this.item.title}</div>
<div class="teaser__intro">${this.item.intro || ''}</div>
<div class="teaser__byline">${this.item.byline?.html}</div>
<div class="teaser__ref">#${number} – ${this.item.section}</div>
`;
	}

	renderDeepDive() {
		this.deepDiveEl.classList.add('deep-dive');
		this.deepDiveEl.innerHTML = `
<h1 class="deep-dive__header"><a href="${this.item.permalink}">${this.item.title}</a></h1>
<div class="deep-dive__overview">
	<h3>Sakskomplekset</h3>
	<p>Her kommer en oppsummering som setter denne artikkelen inn i et større sakskompleks.</p>
	<p><i>42-bussen i Oslo har vært gjenstand for betydelig oppmerksomhet de siste månedene. Hovedsakene har dreid seg om overfylte busser, misnøye blant reisende, kapasitetsproblemer, og et økt press på Ruter for å forbedre busstilbudet på denne linjen. Flere reisende opplever situasjonen som uholdbar og vurderer alternative reisemåter dersom ikke situasjonen bedres.</i></p>
</div>
<div class="deep-dive__tags">
<h3>Tags</h3>
${(this.item.tags || [])
	.map((tag) => {
		return `<a href="${this.readerEdition.getPath()}?tag=${tag.urlPattern}">${tag.displayName}</a>`;
	})
	.join(' / ')}
</div>
<div class="deep-dive__bylines">
	<h3>Bylines</h3>
		${this.item.byline?.items
			.map(
				(byline) => `
	<div class="deep-dive__byline">
	${byline.photoUrl ? `<div class="deep-dive__byline__photo"><img src="${byline.photoUrl}"/></div>` : ''}
		${div(byline.name, 'deep-dive__byline__name')}
		${div(byline.email, 'deep-dive__byline__email')}
		${div(byline.bio || 'Here is a text about this author, and links to other stories by the author', 'deep-dive__byline__bio')}
</div>
		 `
			)
			.join('\n')}
</div>
<div class="deep-dive__related-articles">
	<h3>Related articles</h3>
</div>
`;
		const relatedArticlesEl = this.deepDiveEl.querySelector('.deep-dive__related-articles') as HTMLDivElement;
		const mainArticle = this.item;
		const locale = this.readerEdition.locale;
		if (this.item instanceof Article) {
			renderRelatedArticles(this.readerEdition.loadRelatedArticles(this.item));
		}

		function renderRelatedArticles(relatedArticlesPromise: Promise<RelatedArticle[]>) {
			relatedArticlesPromise.then((relatedArticles) => {
				relatedArticles.forEach((related) => {
					const permalink = mainArticle.createPermalink(related.id);
					const el = createElement(relatedArticlesEl.ownerDocument, 'div', 'deep-dive__related-article');
					el.innerHTML = `
		${related.assetUrl ? `<div class="deep-dive__related-article__asset"><img src="${related.assetUrl}"/></div>` : ''}
		<div class="deep-dive__related-article__title"><a href="${permalink}">${related.title}</a></div>
		<div class="deep-dive__related-article__intro">${related.intro || ''}</div>
		<div class="deep-dive__related-article__published">${fullDate(related.publishedAt, locale)}</div>
`;
					relatedArticlesEl.appendChild(el);
				});
			});
		}
	}

	async load() {
		if (this.loadArticlePromise) {
			return this.loadArticlePromise;
		}
		this.loadArticlePromise = this.readerEdition.loadFullArticle(
			this.item,
			this.item.groupBy,
			this.item.sortOrder,
			this.store
		);
		const article = await this.loadArticlePromise;
		if (article) {
			this.item = article;
			if (article.cardLayouts) {
				this.desktopCardLayouts = article.cardLayouts.desktop;
				this.mobileCardLayouts = article.cardLayouts.mobile;
			}
			this.cards.item = article;
		}
	}

	async loadAndRender() {
		this.renderPromise = new Promise((resolve) => {
			this.load().then(() => {
				this.render();
				resolve(true);
			});
		});
		await this.renderPromise;
	}

	render() {
		this.el.classList.add('article');
		this.cardsEl.classList.add('cards');
		this.el.dataset.id = this.item.id;
		this.el.dataset.number = String(this.number);
		this.el.style.setProperty('--number', String(this.number));
		this.applyArticleState();
		this.el.setAttribute('lang', 'no');
		const category = this.item.category || this.item.section || '';
		this.el.dataset.category = category.toLocaleLowerCase();
		const number = this.number;
		const total = this.readerEdition.articles.length;
		const locale = this.readerEdition.locale;

		this.el.innerHTML = [
			renderProgress(),
			renderStaticTitle(this.item.story, this.item.title),
			renderTags(this.item),
			renderArticleActions()
		].join('\n');

		this.cards.renderCards(this.getCardLayouts());
		this.el.dataset.cardCount = String(this.cards.cardCount);
		this.el.style.setProperty('--card-count', String(this.cards.cardCount));
		this.el.appendChild(this.cardsEl);
		if (this.mobileMode.matches) {
			// Only apply on mobile where horizontal scrolling is used
			this.cleanupScrollFix = setUpNestedScrollFix(this.cardsEl);
		}

		this.el.querySelectorAll('[data-set-article-state]').forEach((el) => {
			el.addEventListener('click', (event) => this.handleStateEvent(event));
		});

		// this.cards.cloneCardByNumber(1);

		function renderProgress() {
			return `<div class="article-progress"></div>`;
		}

		function renderStaticTitle(story: string, title: string) {
			return div((story ? `${story}: ` : '') + title, 'article__static-title');
		}

		function renderTags(article: ReaderItem) {
			return div(
				[dateTime(article.publishedAt, locale), article.category || article.section]
					.filter((t) => t)
					.join(' / '),
				'tags'
			);
		}

		function renderArticleActions() {
			type ActionButtonConfig = [state: string, icon: string, label: string, onIcon?: string, onLabel?: string];

			const actions: ActionButtonConfig[] = [
				// ['deep-dive', '🧐', 'Deep dive'],
				['starred', '☆', 'Star', '★', 'Starred']
				// ['done', '↓', 'Done']
			];

			return `<div class="article-actions">
<div data-action="show-toc" class="button article-action"><span class="button__label">${number}/${total}</span></div>
${actions.map(stateButton).join('')}
<div data-action="toggle-layout-menu" class="button article-action"><span class="action-icon action-icon--layout">${layoutIcon}</span> <span class="button__label">Layout</span></div>
<div data-action="next-article" class="button article-action"><span class="button__label">Next</span> <span class="button__extra">↓</span></div>
<div data-action="previous-card" class="button article-action"><span class="button__label">Previous page</span> <span class="button__extra">←</span></div>
<div data-action="next-card" class="button article-action"><span class="button__label">Next page</span> <span class="button__extra">→</span></div>
</div>`;

			function stateButton(options: ActionButtonConfig) {
				const [state, icon, label, onIcon, onLabel] = options;

				const iconHtml = onIcon
					? `<span class="action-icon action-icon__on">${onIcon}</span>
			<span class="action-icon action-icon__off">${icon}</span>`
					: `<span class="action-icon">${icon}</span>`;
				const labelHtml = onLabel
					? `<span class="action-label action-label__on">${onLabel}</span>
			<span class="action-label action-label__off">${label}</span>`
					: `<span class="action-label">${label}</span>`;

				return `<div class="button article-action" data-set-article-state="${state}">${iconHtml}${labelHtml}</div>`;
			}
		}
	}

	getAvailableLayoutsForCurrentCard(): Record<string, {layouts: string[]; currentIndex: number}> {
		const cardNumber = this.getCurrentCardNumber();
		const cardEl = this.getCardEl(cardNumber);
		const mode = this.el.closest('[data-mode]')?.getAttribute('data-mode') || 'default';
		const isMobile = this.mobileMode.matches;
		const cardChars = Number.parseInt(cardEl?.dataset.chars || '0', 10);
		const figureCount = Number.parseInt(cardEl?.dataset.figureCount || '0', 10);
		const currentLayout = this.getActiveCardLayout();

		const layoutGroups = getAvailableLayoutsForCard(cardNumber, mode, isMobile, cardChars, figureCount);

		// Find current layout index in each group
		const result: Record<string, {layouts: string[]; currentIndex: number}> = {};
		for (const [key, layouts] of Object.entries(layoutGroups)) {
			const currentIndex = currentLayout ? layouts.indexOf(currentLayout) : -1;
			result[key] = {layouts, currentIndex};
		}

		return result;
	}

	private generateLayoutMenuContent(): string {
		const layoutGroups = this.getAvailableLayoutsForCurrentCard();

		if (Object.keys(layoutGroups).length === 0) {
			return '<div class="layout-menu__header">Select layout</div><div class="layout-menu__empty">No layout options available for this card</div>';
		}

		const currentLayout = this.getActiveCardLayout();
		const groupsHtml = Object.entries(layoutGroups)
			.map(([_groupKey, {layouts}]) => {
				const layoutsHtml = layouts
					.map((layout) => {
						const isActive = layout === currentLayout;
						const displayName = this.formatLayoutName(layout);
						return `<div class="layout-menu__item ${isActive ? 'layout-menu__item--active' : ''}" data-action="set-card-layout" data-action-value="${layout}">${displayName}</div>`;
					})
					.join('');

				return `<div class="layout-menu__group">
					${layoutsHtml}
				</div>`;
			})
			.join('');

		return `<div class="layout-menu__header">Select layout</div>${groupsHtml}
			<div class="layout-menu__item layout-menu__item--reset" data-action="set-card-layout" data-action-value="">Reset to default</div>`;
	}

	private formatLayoutName(layout: string): string {
		if (!layout) return 'Default';
		return layout
			.split('-')
			.filter((word) => word !== 'pressflow')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	toggleLayoutMenu() {
		this.layoutMenuEl.hidden = !this.layoutMenuEl.hidden;
	}

	closeLayoutMenu() {
		this.layoutMenuEl.hidden = true;
	}

	updateLayoutMenuPopover() {
		this.layoutMenuEl.innerHTML = this.generateLayoutMenuContent();

		if (!this.layoutMenuEl.parentElement) {
			this.el.append(this.layoutMenuEl);
		}
	}

	cleanup() {
		if (this.cleanupScrollFix) {
			this.cleanupScrollFix();
			this.cleanupScrollFix = null;
		}
	}

	registerEventListeners() {
		this.tocEntryEl.addEventListener('click', (event) => {
			event.stopImmediatePropagation();
			if (this.isActive) {
				this.el.scrollIntoView({
					block: 'start',
					inline: 'start',
					behavior: 'smooth'
				});
			} else {
				this.onActivate(this);
			}
		});

		this.frontPageEl.addEventListener('click', (event) => {
			event.stopImmediatePropagation();
			this.onActivate(this);
		});

		this.el.addEventListener('card:activated', (_event: Event) => {
			this.updateReadingProgressIndicators();
		});
	}

	isOnFirstCard(): boolean {
		return this.cards.currentCardNumber === 1;
	}

	isOnLastCard(): boolean {
		return this.cards.currentCardNumber === this.cards.cardCount;
	}

	getCurrentCardEl() {
		return this.cards.getCurrentCardEl();
	}

	resetSwipeProgress() {
		const currentCard = this.getCurrentCardEl();
		if (currentCard) {
			currentCard.style.translate = '';
		}

		const prevCard = this.cards.getCardElByNumber(this.getCurrentCardNumber() - 1);
		if (prevCard) {
			prevCard.style.translate = '';
		}

		const nextCard = this.cards.getCardElByNumber(this.getCurrentCardNumber() + 1);
		if (nextCard) {
			nextCard.style.translate = '';
		}
	}

	// TODO: Handle reversed swipe gesture
	renderSwipeProgress(progress: number) {
		if (progress < -1 || progress > 1) {
			return;
		}

		const currentCard = this.getCurrentCardEl();
		if (!currentCard) {
			return;
		}

		const currentCardOffset = progress * -100;
		const translateX = this.isOnFirstCard() ? Math.min(0, currentCardOffset) : currentCardOffset;
		currentCard.style.translate = `${translateX}%`;

		if (progress < 0 && this.hasPreviousCard()) {
			const prevCard = this.cards.getCardElByNumber(this.getCurrentCardNumber() - 1);
			if (prevCard) {
				const offset = (1 + progress) * -100;
				prevCard.style.translate = `calc(${offset}% - 5px)`;
			}
		}

		if (progress > 0 && this.hasNextCard()) {
			const nextCard = this.cards.getCardElByNumber(this.getCurrentCardNumber() + 1);
			if (nextCard) {
				const offset = (1 - progress) * 100;
				nextCard.style.translate = `calc(${offset}% + 5px)`;
			}
		}
	}

	activateCard(targetCardNumber: number, skipScroll = false) {
		this.cards.activateCard(targetCardNumber, skipScroll);
		this.hideDeepDive();
		this.updateReadingProgressIndicators();
		this.updateLayoutMenuPopover();
	}

	redrawCards(mode: string) {
		this.postProcessCards(mode, true);
	}

	postProcessCards(mode: string, forceRedraw = false) {
		let removeAfter = false;
		if (!this.el.parentElement) {
			// Desktop mode for non-active article
			this.currentViewEl.append(this.el);
			removeAfter = true;
		}

		if (mode === 'pressflow') {
			this.cards.postProcessCardsWithPressflow(this.getCardLayouts(), forceRedraw);
		} else {
			this.cards.postProcessCards(this.getCardLayouts(), forceRedraw);
		}
		this.updateTocDots();
		this.updateReadingProgressIndicators();

		if (removeAfter) {
			this.removeFromCurrentViewEl();
		}
	}

	setActiveCardLayout(layout: CardLayout, mode: string) {
		const currentCardNumber = this.getCurrentCardNumber();
		const cardLayouts = this.getCardLayouts();
		cardLayouts[currentCardNumber] = layout;
		this.store.setArticleCardLayouts({
			id: this.item.id,
			desktop: this.desktopCardLayouts,
			mobile: this.mobileCardLayouts
		});
		// TODO: Handle prechunked content
		this.render();
		this.postProcessCards(mode, true);
		this.activateCard(currentCardNumber);
	}

	getCardChunk(cardNumber: number) {
		return this.cards.getCardChunk(cardNumber);
	}

	getCardEl(cardNumber: number) {
		return this.cards.getCardElByNumber(cardNumber);
	}

	getActiveCardLayout() {
		const cardLayouts = this.getCardLayouts();
		return cardLayouts[this.getCurrentCardNumber()];
	}

	updateReadingProgressIndicators() {
		this.el.querySelectorAll<HTMLElement>('.article-progress__page').forEach((el, index) => {
			el.dataset.cardState = index < this.cards.currentCardNumber ? 'past' : 'future';
		});
	}

	getCurrentCardNumber() {
		return this.cards.currentCardNumber;
	}

	previousCard() {
		this.activateCard(this.cards.currentCardNumber - 1);
	}

	nextCard() {
		this.activateCard(this.cards.currentCardNumber + 1);
	}

	hasPreviousCard() {
		return this.cards.currentCardNumber > 1;
	}

	hasNextCard() {
		return this.cards.cardCount >= this.cards.currentCardNumber + 1;
	}

	handleStateEvent(event: Event) {
		event.stopImmediatePropagation();
		const target = event.target as HTMLDivElement;
		const state = target.dataset.setArticleState as ItemStateValue;
		if (state === 'deep-dive') {
			this.toggleDeepDive();
			return;
		}

		this.onStateChange(this, state, true);
	}

	toggleDeepDive() {
		if (this.deepDiveEl.parentElement === this.el) {
			this.hideDeepDive();
		} else {
			this.showDeepDive();
		}
	}

	showDeepDive() {
		if (!this.deepDiveEl.classList.contains('deep-dive')) {
			this.renderDeepDive();
		}
		this.el.appendChild(this.deepDiveEl);
	}

	hideDeepDive() {
		if (this.deepDiveEl.parentElement === this.el) {
			exitWithTransition(this.deepDiveEl, 'deep-dive--exit');
		}
	}

	setNext(listing: ReaderListing | undefined) {
		this.nextListing = listing;
	}

	setPrevious(listing: ReaderListing | undefined) {
		this.previousListing = listing;
	}

	applyArticleState() {
		this.el.dataset.state = this.item.state || 'new';
		this.tocEntryEl.dataset.state = this.item.state || 'new';
		this.frontPageEl.dataset.state = this.item.state || 'new';

		this.el.dataset.starred = String(this.item.starred);
		this.tocEntryEl.dataset.starred = String(this.item.starred);
		this.frontPageEl.dataset.starred = String(this.item.starred);
	}

	updateTocDots() {
		this.tocEntryEl.dataset.cards = String(this.cards.cardCount);

		const progress = this.mobileMode.matches ? this.item.mobileProgress : this.item.desktopProgress;
		const tocEntrySeenEl = this.tocEntryEl.querySelector('.seen');
		if (tocEntrySeenEl instanceof HTMLElement) {
			tocEntrySeenEl.innerHTML = '<span class="dot"></span>'.repeat(Math.max(0, progress));
		}
		const tocEntryUnseenEl = this.tocEntryEl.querySelector('.unseen');
		if (tocEntryUnseenEl instanceof HTMLElement) {
			tocEntryUnseenEl.innerHTML = '<span class="dot"></span>'.repeat(
				Math.max(0, this.cards.cardCount - progress)
			);
		}
	}

	async currentArticleMode(mode: string) {
		this.el.classList.add('article--active');
		this.isActive = true;
		if (this.el.parentNode !== this.currentViewEl) {
			this.currentViewEl.appendChild(this.el);
		}

		if (this.el.dataset.state === 'new') {
			this.store.setItemState(this.item, 'seen');
			this.applyArticleState();
		}

		this.tocEntryEl.classList.add('toc-entry--active');
		await this.renderPromise;
		this.updateReadingProgressIndicators();

		this.postProcessCards(mode);
	}

	getCardLayouts() {
		return this.mobileMode.matches ? this.mobileCardLayouts : this.desktopCardLayouts;
	}

	addToCurrentViewEl() {
		this.currentViewEl.appendChild(this.el);
		this.updateReadingProgressIndicators();
	}

	markAsNotActive() {
		if (this.cards.isOnLastCard() && this.item.getState().state !== 'done') {
			this.store.setItemState(this.item, 'done');
			this.applyArticleState();
		}
		this.cards.deactivateCurrentCard();
		this.cards.removeActiveDialog();
		this.cards.disconnectIntersectionObserver();
		this.el.classList.remove('article--active');
		this.isActive = false;
		this.tocEntryEl.classList.remove('toc-entry--active');
	}

	removeFromCurrentViewEl() {
		if (this.el.parentElement === this.currentViewEl) {
			this.currentViewEl.removeChild(this.el);
		}
	}
}
